import { Box, Button, CircularProgress } from "@mui/material";
import { FC, useState } from "react";
import { useShallow } from "zustand/shallow";
import { useCollectionDialogStore } from "./stores/CollectionDialogStore";
import {
  createCollection,
  updateCollection,
} from "../../../../apis/bacchus/collections";
import { useSnackbar } from "../../../../components/Snackbar";
import { useCollectionsContext } from "../hooks/useCollectionsContext";
import { LightTooltip } from "../../../../components/LightTooltip";

export const Actions: FC = () => {
  const { setTypeFilter, loadCollections, setSearchQuery } =
    useCollectionsContext();
  const {
    name,
    description,
    type,
    country,
    items,
    editingCollectionId,
    setIsVisible,
  } = useCollectionDialogStore(
    useShallow((state) => ({
      name: state.name,
      description: state.description,
      type: state.type,
      country: state.country,
      items: state.items,
      editingCollectionId: state.editingCollectionId,
      setIsVisible: state.setIsVisible,
    }))
  );
  const { showSuccess, showError } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const isSaveButtonDisabled = !name || !description || !items.length;

  const handleCancel = () => {
    setIsVisible(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const isEditing = !!editingCollectionId;

    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        visibility: "private" as const,
        blob: {
          ids: items,
          ...(country ? { countryId: country } : {}),
        },
      };

      let result;
      if (isEditing) {
        result = await updateCollection(type, editingCollectionId, payload);
      } else {
        result = await createCollection(type, payload);
      }

      if (result.error) {
        showError(`Failed to ${isEditing ? "update" : "create"} collection`);
        return;
      }

      setSearchQuery("");
      setTypeFilter("all");
      setIsVisible(false);
      showSuccess(
        `Collection ${isEditing ? "updated" : "created"} successfully!`
      );
      await loadCollections();
    } catch (error) {
      showError(`Failed to ${isEditing ? "update" : "create"} collection`);
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="flex-end"
      alignItems="center"
      gap={2.5}
      padding="18px 20px"
      sx={{
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Button
        data-testid="collections-cancel-btn"
        variant="text"
        color="secondary"
        onClick={handleCancel}
        disabled={isSaving}
      >
        Cancel
      </Button>
      <LightTooltip
        title={
          isSaveButtonDisabled
            ? "Please fill in all required fields to save"
            : ""
        }
        placement="top"
      >
        <span>
          <Button
            data-testid="collection-dialog-save-btn"
            variant="contained"
            disabled={isSaveButtonDisabled || isSaving}
            onClick={handleSave}
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {isSaving ? "Saving..." : editingCollectionId ? "Update" : "Save"}
          </Button>
        </span>
      </LightTooltip>
    </Box>
  );
};
